import {
  ActivityMonitor,
  sessionManagement,
  commonActions,
  authenticationContext,
  HttpMethods,
  loggerFactory
} from '@codametrix/ui-common';
import { requestJSON } from '@codametrix/client-api';
import { sendActivityHeartbeat } from '@codametrix/client-session';
import { getSessionHeartbeatIntervalMs } from '@codametrix/client-runtime';
import store from '../store/store';
import { dismissModal, showModalMessage } from '../actions/ui';
import { shouldRefresh, tokenAvailable } from '../actions/contexts.action';
import { logoutApp } from '../actions/login.action';
import history from '../history';
import { Action } from 'redux';
import { dispatchSessionHeartbeatMiss } from './session-heartbeat-miss';

const { activityMonitor } = sessionManagement;

const logger = loggerFactory('SessionManagement');

const onSessionHeartbeatMiss = () => dispatchSessionHeartbeatMiss(store.dispatch);

/**
 * JWT renewal via PUT /contexts/v1. The session heartbeat renews the cookie
 * session and CSRF token but does not yet return a JWT or TTL, so
 * BEGIN_REFRESH must still mint a fresh token. Dispatching tokenAvailable
 * routes the result through auth-middleware, which applies the token, re-arms
 * the activity timers from the new TTL, and keeps Redux and sessionStorage in
 * step for the context-switch reload path.
 */
const renewContextToken = async (contextId: number) => {
  try {
    const freshContext = await requestJSON<CMxAPI.ContextAPI>(`/contexts/v1`, {
      method: HttpMethods.PUT,
      body: JSON.stringify({ organizationId: contextId })
    });

    store.dispatch(
      tokenAvailable({
        token: freshContext.jwtToken,
        expiryTimeToLive: freshContext.expiryTimeToLive
      }) as Action
    );
  } catch (e) {
    // Leave the existing timers running; an unrenewed token expires into the
    // normal TIMEOUT path rather than forcing an extra logout route here.
    logger.error(`context renewal failed`, e);
  }
};

/**
 * Retrieve a new token for a session.
 */
const refreshContext = async () => {
  const contextId = authenticationContext.getContextId();
  if (contextId == null) {
    return;
  }

  store.dispatch(dismissModal() as Action);
  const shouldBroadcastRefresh = activityMonitor.shouldBroadcastRefreshToPeers();
  if (shouldBroadcastRefresh) {
    store.dispatch(shouldRefresh() as Action);
  }

  await renewContextToken(contextId);

  const shouldSendHeartbeat =
    shouldBroadcastRefresh &&
    activityMonitor.isActiveSince(
      Date.now() - getSessionHeartbeatIntervalMs()
    );
  await sendActivityHeartbeat({
    contextId,
    shouldSendHeartbeat,
    onSessionMiss: onSessionHeartbeatMiss,
  });
};

/**
 * Log the user out of the application
 */
const doLogout = () => {
  history.push('/');
};
/**
 * Handle inactivity from the user
 */
const handleTimeout = () => {
  store.dispatch(dismissModal() as Action);
  store.dispatch((logoutApp({ showModal: false }) as any) as Action);
};

/**
 * Prompt for user to refresh session.
 */

const handleActivityPrompt = () => {
  store.dispatch(
    showModalMessage({
      title: 'Are you still there?',
      buttonText: 'Yes',
      handler: commonActions.UserInterface.REFRESH
    }) as Action
  );
};

activityMonitor.on(
  ActivityMonitor.events.ACTIVITY_PROMPT,
  handleActivityPrompt
);
activityMonitor.on(ActivityMonitor.events.BEGIN_REFRESH, refreshContext);
activityMonitor.on(ActivityMonitor.events.LOGOUT, doLogout);
activityMonitor.on(ActivityMonitor.events.TIMEOUT, handleTimeout);
